import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, onSnapshot, doc, getDoc, updateDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, Grid, Chip, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Divider, Alert, Snackbar,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import TvIcon from '@mui/icons-material/Tv';
import QueuePlayNextIcon from '@mui/icons-material/QueuePlayNext';
import LinkIcon from '@mui/icons-material/Link';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LagbasLogo from '../components/LagbasLogo';

function getDisplayStatus(display) {
  if (!display.castId) return 'WAITING';
  const ls = display.lastSeen?.toDate?.() || null;
  if (ls && (Date.now() - ls.getTime()) < 120000) return 'ONLINE';
  return 'OFFLINE';
}

function StatusDot({ status }) {
  const colors = { ONLINE: '#4caf50', OFFLINE: '#555', WAITING: '#FF6B00' };
  return (
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors[status] || '#555', flexShrink: 0 }} />
  );
}

const STEPS = [
  {
    num: 1,
    icon: '🖥️',
    title: 'Koppla en skärm',
    desc: 'Öppna valfri webbläsare på TV:n eller skärmen och gå till adressen som visas i systemet. Skärmen visar en 6-siffrig kod.',
    action: { label: 'Koppla skärm', path: '/castn/connect' },
  },
  {
    num: 2,
    icon: '🎬',
    title: 'Skapa ett bildspel (Cast)',
    desc: 'Ett bildspel är en spellist med anslag. Skapa ett bildspel och ge det ett namn – du kan ha ett per skärm eller dela samma på flera.',
    action: { label: 'Hantera Casts', path: '/marketing/castn/casts' },
  },
  {
    num: 3,
    icon: '🃏',
    title: 'Lägg till anslag',
    desc: 'Anslag är innehållet – text, bilder, väder, avgångar, referenser m.m. Varje anslag tilldelas en zon och en visningstid.',
    action: { label: 'Hantera Casts', path: '/marketing/castn/casts' },
  },
  {
    num: 4,
    icon: '🔗',
    title: 'Tilldela bildspelet till skärmen',
    desc: 'Gå till Skärmar, välj din skärm och koppla det bildspel du skapat. Skärmen börjar spela direkt.',
    action: { label: 'Hantera Skärmar', path: '/marketing/castn/displays' },
  },
];

const THEMES = [
  { value: 'tikab',  label: 'Tikab (orange)', preview: '#FF6B00' },
  { value: 'nordic', label: 'Nordic (blå)',   preview: '#2196F3' },
  { value: 'dark',   label: 'Dark (grå)',     preview: '#aaaaaa' },
  { value: 'light',  label: 'Light (vit)',    preview: '#333333' },
  { value: 'forest', label: 'Forest (grön)',  preview: '#4caf50' },
];

const LAYOUTS = [
  { value: 'fullscreen', label: 'Helskärm', icon: '⬛' },
  { value: 'two-zone',   label: 'Huvud + Sidofält', icon: '▪️▫️' },
  { value: 'three-zone', label: 'Huvud + Sido + Sidfot', icon: '⊞' },
];

const PANEL_STYLES = [
  { value: 'classic', label: 'Klassisk' },
  { value: 'glass',   label: 'Glas (transparent)' },
];

export default function CastNDashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const companyId = userProfile?.companyId || '';

  const [displays, setDisplays] = useState([]);
  const [casts, setCasts] = useState([]);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Global CastN config (stored on company doc)
  const [config, setConfig] = useState({
    defaultTheme: 'tikab',
    defaultLayout: 'fullscreen',
    defaultPanelStyle: 'classic',
    defaultSidfotColumns: 1,
    defaultSidoRows: 1,
  });
  const [configSaved, setConfigSaved] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);

  // Load displays, casts, slides
  useEffect(() => {
    if (!companyId) return;
    const q1 = query(collection(db, 'displays'), where('companyId', '==', companyId));
    const unsub1 = onSnapshot(q1, snap => {
      setDisplays(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const q2 = query(collection(db, 'casts'), where('companyId', '==', companyId));
    const unsub2 = onSnapshot(q2, snap => {
      setCasts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const q3 = query(collection(db, 'slides'), where('companyId', '==', companyId));
    const unsub3 = onSnapshot(q3, snap => {
      setSlides(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [companyId]);

  // Load config from company doc
  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'companies', companyId)).then(snap => {
      if (snap.exists() && snap.data().castNConfig) {
        setConfig(prev => ({ ...prev, ...snap.data().castNConfig }));
      }
    });
  }, [companyId]);

  function handleConfigChange(field, value) {
    setConfig(prev => ({ ...prev, [field]: value }));
    setConfigDirty(true);
  }

  async function saveConfig() {
    if (!companyId) return;
    await updateDoc(doc(db, 'companies', companyId), { castNConfig: config });
    setConfigDirty(false);
    setConfigSaved(true);
  }

  const onlineCount = displays.filter(d => getDisplayStatus(d) === 'ONLINE').length;
  const connectedCount = displays.filter(d => d.castId).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <Typography variant="h6" fontWeight="bold">
          Cast<span style={{ color: '#FF6B00' }}>N</span>
          <span style={{ color: '#aaa', fontWeight: 400, fontSize: '0.9rem', marginLeft: 8 }}>– Digital Signage</span>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ArrowBackIcon />} sx={{ color: '#aaa' }} onClick={() => navigate(-1)}>
          Tillbaka
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: '#FF6B00' }} /></Box>
        ) : (
          <>
            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Skärmar totalt', value: displays.length, color: '#1a1a1a' },
                { label: 'Anslutna', value: connectedCount, color: '#2196F3' },
                { label: 'Online nu', value: onlineCount, color: '#4caf50' },
                { label: 'Casts', value: casts.length, color: '#FF6B00' },
                { label: 'Anslag totalt', value: slides.length, color: '#9c27b0' },
              ].map((stat, i) => (
                <Grid item xs={6} sm={4} md={2.4} key={i}>
                  <Paper sx={{ p: 2.5, textAlign: 'center', borderTop: `3px solid ${stat.color}` }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color }}>{stat.value}</Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>{stat.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Instructions */}
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2.5 }}>
                    Hur det fungerar
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {STEPS.map(step => (
                      <Box key={step.num} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        {/* Step number */}
                        <Box sx={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          bgcolor: '#FF6B00', color: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 900, fontSize: '1rem',
                        }}>
                          {step.num}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight="bold" sx={{ fontSize: '0.95rem', mb: 0.3 }}>
                            {step.icon} {step.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
                            {step.desc}
                          </Typography>
                        </Box>
                        <Button size="small" variant="outlined"
                          onClick={() => navigate(step.action.path)}
                          sx={{ flexShrink: 0, color: '#FF6B00', borderColor: '#FF6B00',
                            '&:hover': { bgcolor: 'rgba(255,107,0,0.05)', borderColor: '#e55a00' } }}>
                          {step.action.label}
                        </Button>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ my: 2.5 }} />

                  {/* Quick links */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#444' }}>
                    Snabblänkar
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Chip icon={<QueuePlayNextIcon />} label="Hantera Casts"
                      onClick={() => navigate('/marketing/castn/casts')}
                      sx={{ bgcolor: '#FF6B00', color: 'white', fontWeight: 'bold', cursor: 'pointer',
                        '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#e55a00' } }} />
                    <Chip icon={<TvIcon />} label="Hantera Skärmar"
                      onClick={() => navigate('/marketing/castn/displays')}
                      sx={{ bgcolor: '#2196F3', color: 'white', fontWeight: 'bold', cursor: 'pointer',
                        '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#1976d2' } }} />
                    <Chip icon={<LinkIcon />} label="Koppla skärm"
                      onClick={() => navigate('/castn/connect')}
                      sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold', cursor: 'pointer',
                        '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#388e3c' } }} />
                    <Chip icon={<WallpaperIcon />} label="Bakgrundsbilder"
                      onClick={() => navigate('/marketing/castn/backgrounds')}
                      sx={{ bgcolor: '#7b1fa2', color: 'white', fontWeight: 'bold', cursor: 'pointer',
                        '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#6a1b9a' } }} />
                  </Box>
                </Paper>
              </Grid>

              {/* Config */}
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2.5 }}>
                    Standardinställningar
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888', mb: 2.5, lineHeight: 1.5 }}>
                    Dessa värden används som standard när du skapar nya skärmar och bildspel. Du kan alltid ändra per skärm eller cast.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Default theme */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Standardtema</InputLabel>
                      <Select
                        value={config.defaultTheme}
                        label="Standardtema"
                        onChange={e => handleConfigChange('defaultTheme', e.target.value)}
                      >
                        {THEMES.map(t => (
                          <MenuItem key={t.value} value={t.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: t.preview, flexShrink: 0 }} />
                              {t.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Default layout */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Standardlayout</InputLabel>
                      <Select
                        value={config.defaultLayout}
                        label="Standardlayout"
                        onChange={e => handleConfigChange('defaultLayout', e.target.value)}
                      >
                        {LAYOUTS.map(l => (
                          <MenuItem key={l.value} value={l.value}>{l.icon} {l.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Panel style */}
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                        Panelstil
                      </Typography>
                      <ToggleButtonGroup
                        exclusive size="small" fullWidth
                        value={config.defaultPanelStyle}
                        onChange={(_, v) => v && handleConfigChange('defaultPanelStyle', v)}
                      >
                        {PANEL_STYLES.map(s => (
                          <ToggleButton key={s.value} value={s.value}
                            sx={{ fontSize: '0.8rem',
                              '&.Mui-selected': { bgcolor: 'rgba(255,107,0,0.12)', color: '#FF6B00', fontWeight: 'bold' } }}>
                            {s.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>

                    {/* Sidfot columns */}
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                        Sidfot – antal kolumner (standard)
                      </Typography>
                      <ToggleButtonGroup
                        exclusive size="small" fullWidth
                        value={config.defaultSidfotColumns}
                        onChange={(_, v) => v && handleConfigChange('defaultSidfotColumns', v)}
                      >
                        {[1, 2, 3, 4].map(n => (
                          <ToggleButton key={n} value={n}
                            sx={{ '&.Mui-selected': { bgcolor: 'rgba(255,107,0,0.12)', color: '#FF6B00', fontWeight: 'bold' } }}>
                            {n}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>

                    {/* Sido rows */}
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                        Sidofält – antal rader (standard)
                      </Typography>
                      <ToggleButtonGroup
                        exclusive size="small" fullWidth
                        value={config.defaultSidoRows}
                        onChange={(_, v) => v && handleConfigChange('defaultSidoRows', v)}
                      >
                        {[1, 2, 3].map(n => (
                          <ToggleButton key={n} value={n}
                            sx={{ '&.Mui-selected': { bgcolor: 'rgba(255,107,0,0.12)', color: '#FF6B00', fontWeight: 'bold' } }}>
                            {n}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>

                    <Button
                      variant="contained"
                      startIcon={configDirty ? <SaveIcon /> : <CheckCircleIcon />}
                      onClick={saveConfig}
                      disabled={!configDirty}
                      sx={{
                        bgcolor: configDirty ? '#FF6B00' : '#4caf50',
                        '&:hover': { bgcolor: configDirty ? '#e55a00' : '#388e3c' },
                        '&.Mui-disabled': { bgcolor: '#4caf50', color: 'white', opacity: 0.85 },
                        mt: 0.5,
                      }}
                    >
                      {configDirty ? 'Spara inställningar' : 'Sparat'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Displays list */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Skärmar</Typography>
              {displays.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: '#aaa' }}>
                  <Typography>Inga skärmar än.</Typography>
                  <Button variant="contained" sx={{ mt: 2, bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}
                    onClick={() => navigate('/marketing/castn/displays')}>
                    Lägg till skärm
                  </Button>
                </Box>
              )}
              {displays.map(d => {
                const status = getDisplayStatus(d);
                const castName = casts.find(c => c.id === d.castId)?.name;
                return (
                  <Box key={d.id} sx={{
                    display: 'flex', alignItems: 'center', gap: 2, py: 1.5,
                    borderBottom: '1px solid #eee', '&:last-child': { borderBottom: 'none' },
                  }}>
                    <StatusDot status={status} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight="bold">{d.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace' }}>{d.token}</Typography>
                    </Box>
                    {castName ? (
                      <Chip label={castName} size="small" sx={{ bgcolor: 'rgba(255,107,0,0.1)', color: '#FF6B00' }} />
                    ) : (
                      <Chip label="Ej kopplad" size="small" variant="outlined" sx={{ color: '#aaa', borderColor: '#ddd' }} />
                    )}
                    <Chip
                      label={status === 'ONLINE' ? 'ONLINE' : status === 'WAITING' ? 'VÄNTAR' : 'OFFLINE'}
                      size="small"
                      sx={{ bgcolor: status === 'ONLINE' ? '#4caf50' : status === 'WAITING' ? '#FF6B00' : '#555', color: 'white', fontWeight: 'bold' }}
                    />
                  </Box>
                );
              })}
            </Paper>
          </>
        )}
      </Container>

      <Snackbar
        open={configSaved}
        autoHideDuration={3000}
        onClose={() => setConfigSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setConfigSaved(false)} sx={{ width: '100%' }}>
          Standardinställningar sparade
        </Alert>
      </Snackbar>
    </Box>
  );
}
